import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, IconButton } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddIcon from '@mui/icons-material/Add'
import DashboardService from '@/services/DashboardService'
import DatagridUser from '@/components/UI/Users/DatagridUser'

const service = new DashboardService()

interface LearnerQuestionDetailsProps {
  data: any
  onEdit: (post: any) => void
  onAdd: (post: any) => void
}

const LearnerQuestionDetails: React.FC<LearnerQuestionDetailsProps> = ({
  data,
  onEdit,
  onAdd,
}) => {
  const { t } = useTranslation('translation')

  const [details, setDetails] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const fetchDiscussionDetails = async (id: any, filter: string) => {
    setLoading(true)
    try {
      const res = await service.getDiscussionDetails(id, filter)
      if (res.status === 200) {
        setDetails(res.data.data)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (data?._id) {
      fetchDiscussionDetails(data._id, 'sort_field=created_at&sort_order=dsc')
    }
  }, [data?._id])

  if (!details || details?.posts?.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('No posts yet')}
        </Typography>
      </Box>
    )
  }

  // Combine all posts and add id for DataGrid
  const gridRows =
    details?.posts?.map((p: any) => ({
      id: p._id,
      ...p,
    })) || []

  const columns: GridColDef[] = [
    {
      field: 'text',
      headerName: t('Answer'),
      flex: 2,
      minWidth: 200,
      sortable: true,
      renderCell: ({ row }) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">{row.text}</Typography>
        </Box>
      ),
    },
    {
      field: 'thumbsup_count',
      headerName: t('Feedback'),
      width: 100,
      sortable: true,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <ThumbUpOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary">
            {row?.thumbsup_count || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'author',
      headerName: t('Suggested By'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <DatagridUser
          user={{
            _id: row?.author_id || row?.author_email,
            email: row?.author_email,
            first_name: row?.author_first_name,
            last_name: row?.author_last_name,
            profile_image: row?.author_profile_image,
          }}
          showAvatar
          linkToProfile={false}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      headerAlign: 'center',
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {row.approval_status === 'accepted' ? (
            <IconButton
              size="small"
              onClick={() => onEdit(row)}
              sx={{ padding: '8px' }}
              aria-label={t('Edit')}
            >
              <EditOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={() => onAdd(row)}
              sx={{ padding: '8px' }}
              aria-label={t('Add to knowledge')}
            >
              <AddIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: 'thumbsup_count', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[10, 25]}
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
              py: 1.5,
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f9fafb',
              fontSize: '0.875rem',
              fontWeight: 600,
            },
          }}
          localeText={{
            noRowsLabel: t('No posts yet'),
          }}
        />
      </Box>
    </Box>
  )
}

export default LearnerQuestionDetails
